import { apiClient } from './client';

export type BannerPreset =
  | 'neutral'
  | 'sand'
  | 'gold'
  | 'ember'
  | 'emerald'
  | 'midnight'
  | 'ocean'
  | 'violet';

/** Ручные цвета поверх пресета. Задаются для light и dark независимо. */
export interface BannerVisualOverride {
  colors?: string[];
  fg?: string;
  muted?: string;
  iconBg?: string;
  iconFg?: string;
  border?: string;
}

export interface BannerCustomStyle {
  light?: BannerVisualOverride | null;
  dark?: BannerVisualOverride | null;
}
export type BannerActionType =
  | 'none'
  | 'payment'
  | 'tickets'
  | 'mock_exam'
  | 'topics'
  | 'marathon'
  | 'external_url';
export type BannerAudience = 'all' | 'free' | 'premium';
export type BannerPlatform = 'all' | 'ios' | 'android';

export interface LocalizedText {
  uz?: string;
  ru?: string;
  kiril?: string;
}

export interface Banner {
  id: string;
  name: string;
  title: LocalizedText;
  subtitle: LocalizedText;
  preset: BannerPreset;
  custom_style: BannerCustomStyle | null;
  icon: string;
  action_type: BannerActionType;
  action_payload: string | null;
  audience: BannerAudience;
  platform: BannerPlatform;
  priority: number;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  created_at: string;
  updated_at: string;
  /** Уникальных показов всего */
  impressions_count: number;
  clicks_count: number;
  /** Доля кликнувших, 0..1 */
  ctr: number;
}

export interface CreateBannerDto {
  name: string;
  title: LocalizedText;
  subtitle?: LocalizedText;
  preset: BannerPreset;
  custom_style?: BannerCustomStyle | null;
  icon: string;
  action_type: BannerActionType;
  action_payload?: string | null;
  audience?: BannerAudience;
  platform?: BannerPlatform;
  priority?: number;
  is_active?: boolean;
  starts_at?: string | null;
  ends_at?: string | null;
}

export interface UpdateBannerDto extends Partial<CreateBannerDto> {}

/**
 * Пресеты заданы в приложении — админка только выбирает из того, что вшито в
 * мобильный билд (ADR 0013). Значения обязаны совпадать с
 * mobile/src/components/banners/presets.ts.
 *
 * Три семьи: тихие на токенах темы, яркие с ТЁМНЫМ текстом (белый на жёлтом и
 * оранжевом даёт контраст 2-3:1 и не читается), глубокие с белым текстом.
 * В тёмной теме цвета на шаг глубже — та же насыщенность на чёрном светится.
 */
export interface BannerVisualStyle {
  colors: string[];
  fg: string;
  muted: string;
  iconBg: string;
  iconFg: string;
  border?: string;
}

export interface BannerPresetStyle {
  value: BannerPreset;
  label: string;
  /** Короткая подсказка, когда этот пресет уместен. */
  hint: string;
  light: BannerVisualStyle;
  dark: BannerVisualStyle;
}

const WHITE = '#FFFFFF';
const ON_DEEP = 'rgba(255,255,255,0.92)';
const ON_DEEP_MUTED = 'rgba(255,255,255,0.78)';

export const BANNER_PRESETS: BannerPresetStyle[] = [
  {
    value: 'neutral',
    label: 'Нейтральный',
    hint: 'Информация без нажима: анонсы, новости',
    light: {
      colors: ['#FFFFFF'],
      fg: '#111111',
      muted: '#6B7280',
      iconBg: '#EBEBEA',
      iconFg: '#6B7280',
      border: '#EFEAE0',
    },
    dark: {
      colors: ['#1C1A22'],
      fg: '#F5F3EF',
      muted: '#A8A2B0',
      iconBg: '#2C2833',
      iconFg: '#A8A2B0',
      border: '#33303B',
    },
  },
  {
    value: 'sand',
    label: 'Песок (мягкое золото)',
    hint: 'Спокойное напоминание о премиуме',
    light: {
      colors: ['#FBF3D9'],
      fg: '#4A3B08',
      muted: '#8A6F14',
      iconBg: '#C9A227',
      iconFg: WHITE,
    },
    dark: {
      colors: ['#2B2413'],
      fg: '#F5EAC8',
      muted: '#D9BC57',
      iconBg: '#C9A227',
      iconFg: '#1A1408',
    },
  },
  {
    value: 'gold',
    label: 'Золото (градиент)',
    hint: 'Премиум-акция, скидка на подписку',
    light: {
      colors: ['#F7DE93', '#C9A227'],
      fg: '#3A2E05',
      muted: 'rgba(58,46,5,0.78)',
      iconBg: 'rgba(255,255,255,0.38)',
      iconFg: '#3A2E05',
    },
    dark: {
      colors: ['#E0BE4A', '#A5811A'],
      fg: '#2A2004',
      muted: 'rgba(42,32,4,0.78)',
      iconBg: 'rgba(255,255,255,0.3)',
      iconFg: '#2A2004',
    },
  },
  {
    value: 'ember',
    label: 'Уголь (оранжевый градиент)',
    hint: 'Срочность: акция сгорает, дедлайн',
    light: {
      colors: ['#FFB020', '#F97316'],
      fg: '#431407',
      muted: 'rgba(67,20,7,0.8)',
      iconBg: 'rgba(255,255,255,0.34)',
      iconFg: '#431407',
    },
    dark: {
      colors: ['#E8951A', '#DC5F0E'],
      fg: '#331004',
      muted: 'rgba(51,16,4,0.8)',
      iconBg: 'rgba(255,255,255,0.28)',
      iconFg: '#331004',
    },
  },
  {
    value: 'emerald',
    label: 'Изумруд (зелёный градиент)',
    hint: 'Достижение, хорошая новость, поздравление',
    light: {
      colors: ['#6EE7B7', '#34D399'],
      fg: '#052E22',
      muted: 'rgba(5,46,34,0.8)',
      iconBg: 'rgba(255,255,255,0.42)',
      iconFg: '#052E22',
    },
    dark: {
      colors: ['#4ED4A3', '#1FB582'],
      fg: '#042219',
      muted: 'rgba(4,34,25,0.8)',
      iconBg: 'rgba(255,255,255,0.32)',
      iconFg: '#042219',
    },
  },
  {
    value: 'midnight',
    label: 'Полночь (тёмный градиент)',
    hint: 'Солидно и весомо, как карточка мок-экзамена',
    light: {
      colors: ['#14141C', '#2E2E3E', '#14141C'],
      fg: WHITE,
      muted: 'rgba(255,255,255,0.72)',
      iconBg: 'rgba(255,255,255,0.14)',
      iconFg: WHITE,
    },
    dark: {
      colors: ['#0A0A12', '#23232F', '#0A0A12'],
      fg: WHITE,
      muted: 'rgba(255,255,255,0.68)',
      iconBg: 'rgba(255,255,255,0.12)',
      iconFg: WHITE,
      border: '#33303B',
    },
  },
  {
    value: 'ocean',
    label: 'Океан (синий градиент)',
    hint: 'Нейтральное промо, обучение, новая функция',
    light: {
      colors: ['#2563EB', '#1D4ED8'],
      fg: ON_DEEP,
      muted: ON_DEEP_MUTED,
      iconBg: 'rgba(255,255,255,0.22)',
      iconFg: WHITE,
    },
    dark: {
      colors: ['#1D4ED8', '#1E3A8A'],
      fg: ON_DEEP,
      muted: ON_DEEP_MUTED,
      iconBg: 'rgba(255,255,255,0.18)',
      iconFg: WHITE,
    },
  },
  {
    value: 'violet',
    label: 'Фиолет (градиент)',
    hint: 'AI-функции, что-то новое и необычное',
    light: {
      colors: ['#7C3AED', '#6D28D9'],
      fg: ON_DEEP,
      muted: ON_DEEP_MUTED,
      iconBg: 'rgba(255,255,255,0.22)',
      iconFg: WHITE,
    },
    dark: {
      colors: ['#6D28D9', '#4C1D95'],
      fg: ON_DEEP,
      muted: ON_DEEP_MUTED,
      iconBg: 'rgba(255,255,255,0.18)',
      iconFg: WHITE,
    },
  },
];

/**
 * Итоговое оформление для превью: пресет под тему, поверх — ручные цвета.
 * Повторяет getBannerVisual из мобильного клиента.
 */
export function resolveBannerVisual(
  preset: BannerPreset,
  dark: boolean,
  custom?: BannerCustomStyle | null,
): BannerVisualStyle {
  const def = BANNER_PRESETS.find((p) => p.value === preset) ?? BANNER_PRESETS[0];
  const base = dark ? def.dark : def.light;
  const override = dark ? custom?.dark : custom?.light;
  if (!override) return base;

  const cleaned = Object.fromEntries(
    Object.entries(override).filter(([, v]) => v !== null && v !== undefined && v !== ''),
  );
  return {
    ...base,
    ...cleaned,
    colors: override.colors?.length ? override.colors : base.colors,
  };
}

/** CSS-фон: один цвет — заливка, несколько — градиент. */
export function visualBackground(visual: BannerVisualStyle): string {
  if (visual.colors.length === 1) return visual.colors[0];
  return `linear-gradient(90deg, ${visual.colors.join(', ')})`;
}

export const BANNER_ICONS: Array<{ value: string; label: string }> = [
  { value: 'gift', label: '🎁 Подарок' },
  { value: 'clock', label: '⏰ Часы' },
  { value: 'flame', label: '🔥 Огонь' },
  { value: 'zap', label: '⚡ Молния' },
  { value: 'star', label: '⭐ Звезда' },
  { value: 'check', label: '✅ Галочка' },
  { value: 'alert', label: '⚠️ Внимание' },
  { value: 'book', label: '📘 Книга' },
  { value: 'car', label: '🚗 Машина' },
  { value: 'award', label: '🎓 Диплом' },
  { value: 'percent', label: '％ Процент' },
  { value: 'mail', label: '✉️ Конверт' },
];

export const BANNER_ACTIONS: Array<{ value: BannerActionType; label: string }> = [
  { value: 'none', label: 'Без действия' },
  { value: 'payment', label: 'Экран оплаты' },
  { value: 'tickets', label: 'Билеты' },
  { value: 'mock_exam', label: 'Мок-экзамен' },
  { value: 'topics', label: 'Вопросы по темам' },
  { value: 'marathon', label: 'Марафон' },
  { value: 'external_url', label: 'Внешняя ссылка' },
];

export const BANNER_AUDIENCES: Array<{ value: BannerAudience; label: string }> = [
  { value: 'all', label: 'Все' },
  { value: 'free', label: 'Только без подписки' },
  { value: 'premium', label: 'Только с подпиской' },
];

export const BANNER_PLATFORMS: Array<{ value: BannerPlatform; label: string }> = [
  { value: 'all', label: 'Все' },
  { value: 'android', label: 'Только Android' },
  { value: 'ios', label: 'Только iOS' },
];

export const bannersApi = {
  getAll: async (): Promise<Banner[]> => {
    const response = await apiClient.get<Banner[]>('/admin/banners');
    return response.data;
  },

  create: async (dto: CreateBannerDto): Promise<Banner> => {
    const response = await apiClient.post<Banner>('/admin/banners', dto);
    return response.data;
  },

  update: async (id: string, dto: UpdateBannerDto): Promise<Banner> => {
    const response = await apiClient.patch<Banner>(`/admin/banners/${id}`, dto);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/banners/${id}`);
  },
};
